import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { interpolateTemplate } from "lib/i18n/dictionary";
import { useLocalizedNavigate } from "lib/i18n/navigation";

import { formatPlural } from "./plural";
import { ReadyDesignThumb } from "./ready-design-thumb";
import { TEASER_DESIGN_COUNT } from "./ready-designs";
import { SiteCardIcon } from "./site-assistant-card";
import type { ReadyDesign } from "./site.documents";
import { SiteHeader } from "./site-header";
import { READY_DESIGNS_ROUTE, SITE_CLAUDE_ROUTE, SITE_DESCRIBE_ROUTE } from "./site.routes";
import { SiteStatusBadge } from "./site-status-badge";
import { OwnAssistantLockedCard } from "./site-assistant-card";
import { useSiteEntitlements } from "./site.hooks";
import { useSiteAssistantConnection } from "./use-site-assistant-connection";
import { useSiteTranslations } from "./use-site-translations";

function DescribeSiteCard() {
  const { t } = useSiteTranslations();
  const navigate = useLocalizedNavigate();
  return (
    <Card data-testid="provider-site-ai-designer-card">
      <SoftBox p={3} display="flex" flexDirection="column" gap={2}>
        <SoftBox display="flex" gap={2} alignItems="flex-start">
          <SiteCardIcon name="chat_bubble_outline" />
          <SoftBox>
            <SoftTypography variant="h6">{t.designs.aiTitle}</SoftTypography>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              {t.designs.aiBody}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
        <SoftButton
          variant="outlined"
          color="info"
          fullWidth
          onClick={() => navigate(SITE_DESCRIBE_ROUTE)}
          data-testid="provider-site-describe-button"
        >
          {t.designs.aiAction}
        </SoftButton>
      </SoftBox>
    </Card>
  );
}

// Without the AI designer: the Provider's own Claude; its three steps open behind this card.
function StartWithClaudeCard() {
  const { t } = useSiteTranslations();
  const navigate = useLocalizedNavigate();
  const { connection } = useSiteAssistantConnection();
  const { isLoading: isEntitlementsLoading, ownAssistant } = useSiteEntitlements();
  if (!isEntitlementsLoading && !ownAssistant) {
    return <OwnAssistantLockedCard />;
  }
  return (
    <Card data-testid="provider-site-claude-card">
      <SoftBox p={3} display="flex" flexDirection="column" gap={2}>
        <SoftBox display="flex" gap={2} alignItems="flex-start">
          <SiteCardIcon name="auto_awesome" />
          <SoftBox>
            <SoftTypography variant="h6">{t.assistant.create.title}</SoftTypography>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              {t.assistant.create.body}
            </SoftTypography>
          </SoftBox>
        </SoftBox>
        <SoftButton
          variant="outlined"
          color="info"
          fullWidth
          onClick={() => navigate(SITE_CLAUDE_ROUTE)}
          data-testid="provider-site-claude-steps-button"
        >
          {connection ? t.assistant.createWithClaude : t.assistant.connect}
        </SoftButton>
        <SoftTypography variant="caption" color="text" textAlign="center">
          {t.assistant.create.note}
        </SoftTypography>
      </SoftBox>
    </Card>
  );
}

// No site yet: a Ready design is the main way to one; the AI designer, or the Provider's own Claude
// without it, is the second.
export function SiteStart({
  aiDesigner,
  designs,
}: {
  aiDesigner: boolean;
  designs: ReadyDesign[];
}) {
  const { t, locale } = useSiteTranslations();
  const navigate = useLocalizedNavigate();
  const teaser = designs.slice(0, TEASER_DESIGN_COUNT);

  return (
    <Grid container spacing={3} data-testid="provider-site-start-screen">
      <Grid item xs={12}>
        <SiteHeader
          title={t.title}
          description={t.empty.heading}
          action={<SiteStatusBadge site={null} />}
        />
      </Grid>
      <Grid item xs={12} lg={7}>
        <Card sx={{ height: "100%" }}>
          <SoftBox p={3} display="flex" flexDirection="column" gap={2}>
            <SoftTypography variant="button" color="text" fontWeight="regular">
              {t.designs.startBody}
            </SoftTypography>
            {teaser.length > 0 ? (
              <Grid container spacing={2}>
                {teaser.map((design) => (
                  <Grid item xs={4} key={design.id}>
                    <SoftBox
                      role="button"
                      tabIndex={0}
                      aria-label={interpolateTemplate(t.designs.tileLabel, { name: design.name })}
                      data-testid={`provider-site-teaser-${design.id}`}
                      onClick={() => navigate(READY_DESIGNS_ROUTE)}
                      sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                    >
                      <ReadyDesignThumb uri={design.thumbUrl} crop />
                      <SoftTypography
                        variant="button"
                        fontWeight="medium"
                        noWrap
                        display="block"
                        mt={0.5}
                      >
                        {design.name}
                      </SoftTypography>
                    </SoftBox>
                  </Grid>
                ))}
              </Grid>
            ) : null}
            <SoftButton
              variant="gradient"
              color="info"
              fullWidth
              onClick={() => navigate(READY_DESIGNS_ROUTE)}
              data-testid="provider-site-choose-design"
            >
              <Icon sx={{ mr: 1 }}>dashboard</Icon>
              {t.designs.choose}
            </SoftButton>
            {designs.length > 0 ? (
              <SoftTypography variant="caption" color="text" textAlign="center">
                {formatPlural(locale, t.designs.startCount, designs.length)}
              </SoftTypography>
            ) : null}
          </SoftBox>
        </Card>
      </Grid>
      <Grid item xs={12} lg={5}>
        <SoftBox display="flex" alignItems="center" gap={1.5} mb={2}>
          <Divider sx={{ flexGrow: 1, my: 0 }} />
          <SoftTypography variant="caption" color="text">
            {t.or}
          </SoftTypography>
          <Divider sx={{ flexGrow: 1, my: 0 }} />
        </SoftBox>
        {aiDesigner ? <DescribeSiteCard /> : <StartWithClaudeCard />}
      </Grid>
    </Grid>
  );
}
